import { useCallback, useEffect, useState, useSyncExternalStore } from "react"
import type { TUser } from "@/types"
import { setQueryCache, userAPI } from "@/utils"
import { useAPIResources } from "./query"
import { useAuth } from "./useAuth"

const requestedIds = new Set<string>()
let usersMap = new Map<string, TUser>()
const listeners = new Set<() => void>()

export const usersStore = {
  get: (id: string) => usersMap.get(id),
  has: (id: string) => usersMap.has(id),
  setMany: (users: TUser[]) => {
    if (!users?.length) return
    const next = new Map(usersMap)
    for (const u of users) next.set(u.id, u)
    usersMap = next
    listeners.forEach((cb) => {
      cb()
    })
  },
  subscribe: (cb: () => void) => {
    listeners.add(cb)
    return () => listeners.delete(cb)
  },
  getSnapshot: () => usersMap,
}

export function useUsers() {
  const { user } = useAuth()
  const cache = useSyncExternalStore(usersStore.subscribe, usersStore.getSnapshot, usersStore.getSnapshot)
  const [ready, setReady] = useState(false)

  const {
    data: { data: active = [] } = {},
    isPending,
    refetch,
  } = useAPIResources(userAPI, {
    where: { is_active: true },
    enabled: !!user?.id,
    ...setQueryCache(3600e3 * 24),
  })

  useEffect(() => {
    if (!isPending && active.length) {
      usersStore.setMany(active)
      setReady(true)
    }
  }, [isPending, active])

  const findUser = useCallback(
    (id?: string | null): TUser | null => {
      if (!id) return null
      return cache.get(id) || null
    },
    [cache],
  )

  const findUsers = useCallback(
    (ids: string[]): TUser[] => {
      return ids.map((id) => cache.get(id)).filter((u): u is TUser => !!u)
    },
    [cache],
  )

  return {
    cache,
    ready,
    refetch,
    isPending,
    findUser,
    findUsers,
    users: active,
  }
}

export const useEnsureUsers = (ids: string[]) => {
  const { ready, cache } = useUsers()

  useEffect(() => {
    if (!ready || !ids?.length) return
    const toRequest = ids.filter((id) => id && !cache.has(id) && !requestedIds.has(id))
    if (toRequest.length) {
      toRequest.forEach((id) => {
        id && requestedIds.add(id)
      })
      userAPI
        .fetchMany({ where: { id: { $in: toRequest } } })
        .then(({ data }) => {
          usersStore.setMany(data)
          toRequest.forEach((id) => {
            requestedIds.delete(id)
          })
        })
        .catch(() => {
          toRequest.forEach((id) => {
            requestedIds.delete(id)
          })
        })
    }
  }, [ready, ids.toSorted()])
}
